import React, { FC, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { useDispatch, useSelector } from 'react-redux'

import { Button, RichTextInput } from 'Components'
import HeaderSmallModal from 'Components/HeaderSmallModal/HeaderSmallModal'
import { JournalNotesRecord } from 'Models'
import { currentJournalDateSelector } from 'ReduxStore/journalDay'
import {
  addJournalNotesRecordAction,
  removeJournalNotesRecordAction,
  updateJournalNotesRecordAction
} from 'ReduxStore/journalNotesRecords'
import { modalZIndexPriority } from 'Utils/zIndexPriority'

const JOURNAL_NOTE_MAX_LENGTH = 255

type Props = {
  isOpen: boolean
  note?: JournalNotesRecord
  onClose: () => void
}

const NotesModal: FC<Props> = ({ isOpen, onClose, note }: Props) => {
  const dispatch = useDispatch()

  const selectedDate = useSelector(currentJournalDateSelector)

  const [notes, setNotes] = useState(note?.description || '')

  const [isBusy, setIsBusy] = useState(false)

  const isOverLimit = notes.length > JOURNAL_NOTE_MAX_LENGTH

  useEffect(() => {
    setNotes(note?.description || '')
  }, [note])

  const handleSave = async (): Promise<void> => {
    if (isBusy || isOverLimit) return

    setIsBusy(true)

    try {
      if (note?.id) {
        await dispatch(updateJournalNotesRecordAction(note.id, notes))
      } else {
        await dispatch(addJournalNotesRecordAction(notes, selectedDate))
      }
      onClose()
    } catch (error) {
      console.error('Failed to save note:', error)
    } finally {
      setIsBusy(false)
    }
  }

  const handleClose = (): void => {
    setNotes(note?.description || '')
    onClose()
  }

  const handleDeleteNote = async (): Promise<void> => {
    if (isBusy) return

    setIsBusy(true)

    try {
      if (note?.id) {
        await dispatch(removeJournalNotesRecordAction(note.id))
      }

      onClose()
    } catch (error) {
      console.error('Failed to delete note:', error)
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <Modal style={modalZIndexPriority} isOpen={isOpen} onRequestClose={handleClose} ariaHideApp={false}>
      <div className="modal is-small bg-white p-0">
        <HeaderSmallModal onClose={handleClose}>
          <h2>Notizen</h2>
        </HeaderSmallModal>

        <div className="px-3 pt-0 pb-3">
          <RichTextInput className="is-postit" initialValue={notes} onChange={setNotes} />

          <div className="postit-error">
            {isOverLimit ? `Notizen zu lang: max. ${JOURNAL_NOTE_MAX_LENGTH} Zeichen (inkl. Formatierung)` : undefined}
          </div>

          <div className="row">
            <div className="col-12 col-sm-6">
              <Button id="deleteNotes" theme="secondary" onClick={handleDeleteNote} disabled={isBusy}>
                Löschen
              </Button>
            </div>

            <div className="col-12 col-sm-6">
              <Button id="saveNotes" onClick={handleSave} disabled={isBusy || isOverLimit}>
                Speichern
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default NotesModal
